import React, { useEffect, useState } from "react";
import { ScreenHeader } from "@/components/ScreenHeader";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/api/client";
import { colors, spacing, fonts } from "@/theme";

export default function Profile() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user?.role !== "driver") return;
    (async () => {
      try {
        const st = await api("/me/stats");
        setStats(st);
      } catch (e) { console.error(e); }
    })();
  }, [user]);

  const roleLabel = user?.role === "manager" ? "Manager" : user?.role === "plant" ? "Plant User" : "Driver";

  return (
    <div style={{ flex: 1, backgroundColor: colors.surface }}>
      <ScreenHeader title="Profile" subtitle="Account" />
      <div style={s.body}>
        {user?.role === "driver" && (
          <div data-testid="driver-stats" style={s.statsCard}>
            <div style={s.statsTitle}>My weekly totals</div>
            <div style={s.statRow}>
              <Stat label="This week" value={stats?.this_week ?? "—"} sub={stats?.this_week_label} highlight />
              <Stat label="Last week" value={stats?.last_week ?? "—"} />
              <Stat label="All time" value={stats?.total ?? "—"} />
            </div>
            {stats && Object.keys(stats.this_week_by_yard || {}).length > 0 && (
              <div style={s.yardSplit}>
                <div style={s.yardSplitLabel}>This week by yard</div>
                <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
                  {Object.entries(stats.this_week_by_yard).map(([y, n]) => (
                    <div key={y} style={s.yardPill}>
                      <span style={s.yardPillText}>{y}: {n}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <Row label="Name" value={user?.name || "—"} />
        <Row label="Email" value={user?.email || "—"} />
        <Row label="Role" value={roleLabel} />
      </div>
    </div>
  );
}

function Stat({ label, value, sub, highlight }) {
  return (
    <div style={{ ...s.stat, ...(highlight ? s.statHighlight : {}) }}>
      <div style={{ ...s.statValue, ...(highlight ? { color: colors.onBrandPrimary } : {}) }}>{value}</div>
      <div style={s.statLabel}>{label}</div>
      {sub ? <div style={s.statSub}>{sub}</div> : null}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={s.row}>
      <div style={s.rowLabel}>{label}</div>
      <div style={s.rowValue}>{value}</div>
    </div>
  );
}

const s = {
  body: { padding: spacing.xl, display: "flex", flexDirection: "column", gap: spacing.md, maxWidth: 640 },
  statsCard: { borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, padding: spacing.lg, display: "flex", flexDirection: "column", gap: spacing.md },
  statsTitle: { ...fonts.displayRegular, fontSize: 16, color: colors.onSurface },
  statRow: { display: "flex", gap: spacing.sm },
  stat: { flex: 1, borderWidth: 2, borderStyle: "solid", borderColor: colors.border, padding: spacing.md, backgroundColor: colors.surface, textAlign: "center" },
  statHighlight: { backgroundColor: colors.brandPrimary, borderColor: colors.brandPrimary },
  statValue: { ...fonts.displayRegular, fontSize: 30, color: colors.onSurface, lineHeight: 1 },
  statLabel: { ...fonts.mono, fontSize: 11, color: colors.onSurfaceTertiary, marginTop: 4 },
  statSub: { ...fonts.mono, fontSize: 10, color: colors.onSurfaceTertiary, marginTop: 2 },
  yardSplit: { display: "flex", flexDirection: "column", gap: spacing.sm },
  yardSplitLabel: { ...fonts.mono, fontSize: 11, color: colors.onSurfaceTertiary },
  yardPill: { borderWidth: 2, borderStyle: "solid", borderColor: colors.borderStrong, padding: `6px ${spacing.md}px`, backgroundColor: colors.surface },
  yardPillText: { ...fonts.mono, fontSize: 12, color: colors.onSurface },
  row: { borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, padding: spacing.md, backgroundColor: colors.surfaceSecondary },
  rowLabel: { ...fonts.mono, fontSize: 10, color: colors.onSurfaceTertiary, marginBottom: 4 },
  rowValue: { ...fonts.mono, fontSize: 16, color: colors.onSurface },
};
